/**
 * Secure storage wrapper for sensitive data.
 * Sensitive keys are base64-encoded to reduce plain-text exposure in devtools.
 * Storage tier: localStorage when "Keep me signed in" is on (default — survives tabs/restarts,
 * the server session itself expires after 30 days), sessionStorage otherwise.
 */

const STORAGE_PREFIX = 'lei_';
const PERSIST_KEY = 'lei_persist';

const SENSITIVE_KEYS = new Set([
  'session_token',
  'user',
  'rbac_context',
]);

function getKey(key) {
  return SENSITIVE_KEYS.has(key) ? `${STORAGE_PREFIX}${key}` : key;
}

function encode(value) {
  try { return btoa(unescape(encodeURIComponent(value))); } catch { return value; }
}

function decode(value) {
  try { return decodeURIComponent(escape(atob(value))); } catch { return value; }
}

function persistent() {
  try { return localStorage.getItem(PERSIST_KEY) !== '0'; } catch { return false; }
}

const tiers = () => [localStorage, sessionStorage];

export const secureStorage = {
  /** Choose where new sensitive values go. `false` = this tab only. */
  setPersist(on) {
    try { localStorage.setItem(PERSIST_KEY, on ? '1' : '0'); } catch { /* ignore */ }
  },

  isPersistent: persistent,

  getItem(key) {
    try {
      for (const s of tiers()) {
        const raw = s.getItem(getKey(key));
        if (raw !== null) return SENSITIVE_KEYS.has(key) ? decode(raw) : raw;
      }
      return null;
    } catch {
      return null;
    }
  },

  setItem(key, value) {
    try {
      const stored = SENSITIVE_KEYS.has(key) ? encode(value) : value;
      const target = persistent() ? localStorage : sessionStorage;
      const other = persistent() ? sessionStorage : localStorage;
      target.setItem(getKey(key), stored);
      other.removeItem(getKey(key));
    } catch {
      // Storage full or unavailable
    }
  },

  removeItem(key) {
    try {
      tiers().forEach((s) => s.removeItem(getKey(key)));
    } catch {
      // Ignore
    }
  },

  /** Remove all app-specific items */
  clearSession() {
    try {
      SENSITIVE_KEYS.forEach(key => {
        tiers().forEach((s) => s.removeItem(getKey(key)));
      });
    } catch {
      // Ignore
    }
  },
};

export default secureStorage;
